import Button from "../../button/Button";
import styles from "./CancelSubscriptionOfferV2Modal.module.scss";
import close from "../../../assets/icons/close.svg";
import off from "../../../assets/offers/badge.webp";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { subscriptionData } from "../../../types/details-api-types";
import { useSetRenewalSubscriptionMutation } from "../../../store/services/details-api";
import { formatPriceWithCurrency } from "../../../utils";
import { trackEvent } from "../../../utils/analytics";
import { EVENT_NAMES } from "../../../types/event-names";

interface CancelSubscriptionOfferV2ModalProps {
  subscriptionItem: subscriptionData;
  callbackAction: (action: "deactivate" | "close" | "cancel" | "success", data?: subscriptionData) => void;
}

const CancelSubscriptionOfferV2Modal: React.FC<CancelSubscriptionOfferV2ModalProps> = ({
  callbackAction,
  subscriptionItem,
}) => {
  const [setRenewalSubscription] = useSetRenewalSubscriptionMutation();

  const renewalData = useMemo(() => {
    const totalCharges =
      subscriptionItem?.invoices?.reduce((total, invoice) => {
        if (invoice?.type?.toLowerCase() === "charge") {
          return Number(total.toFixed(2)) + Number(invoice.priceTotal) + Number(invoice.vat);
        }
        return Number(total.toFixed(2));
      }, 0) || 0;

    const renewalPrice = Number(subscriptionItem?.renewalPrice || 0);

    const discountPercent =
      totalCharges > 0 ? Number((((renewalPrice - totalCharges) / renewalPrice) * 100).toFixed(2)) : 0;

    return {
      price: Number(totalCharges.toFixed(2)),
      currency: subscriptionItem?.renewalPriceCurrency || "$",
      date: subscriptionItem?.next_payment ? new Date(subscriptionItem.next_payment) : null,
      percentage: discountPercent > 0 ? discountPercent : 60,
    };
  }, [subscriptionItem]);

  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation();

  const showProcessingModal = async () => {
    setIsLoading(true);
    trackEvent(EVENT_NAMES.SUR_SUBSCRIPTION_CANCELLED_DISCOUNT_ACCEPTED);

    try {
      await setRenewalSubscription({ subscription_id: subscriptionItem?.subscription_id }).unwrap();
    } catch (error) {
      console.error(error);
    }
    setSuccess(true);
    setIsLoading(false);
  };

  const closeModal = () => {
    callbackAction("close", subscriptionItem);
    trackEvent(EVENT_NAMES.SUR_SUBSCRIPTION_CANCELLED_DISCOUNT_DECLINED);
  };

  const noTrust = () => {
    callbackAction("deactivate", subscriptionItem);
    trackEvent(EVENT_NAMES.SUR_SUBSCRIPTION_CANCELLED_DISCOUNT_DECLINED, {
      subscriptionId: subscriptionItem?.subscription_id,
    });
  };

  useEffect(() => {
    trackEvent(EVENT_NAMES.SUR_SUBSCRIPTION_CANCELLED_DISCOUNT_OFFER, {
      subscriptionId: subscriptionItem?.subscription_id,
    });
  }, [subscriptionItem]);

  return (
    <>
      {success ? (
        <div className={styles.root}>
          <div className={styles.root_content}>
            <div className={styles.root_close} onClick={() => closeModal()}>
              <img src={close} alt="close" />
            </div>

            <div className={styles.root_header}>
              <div>
                <p className={styles.root_header_title}>{t("Done, price locked in successfully.")}</p>
              </div>
              <p className={styles.root_header_paragraph}>
                {t(
                  "Your renewal has been updated. You’ll keep all Fortect features and real-time protection without any price increase."
                )}
              </p>
            </div>

            <div className={styles.root_footer}>
              <Button
                onClick={() => closeModal()}
                text={t("Return to My Account")}
                type="primary"
                classNames={styles.root_footer_okMobile}
                isLoader={isLoading}
              />
            </div>
          </div>

          <div className={styles.root_media}></div>
        </div>
      ) : (
        <div className={styles.root}>
          {isLoading && (
            <div
              style={{
                zIndex: 9999,
                position: "absolute",
                top: "0",
                left: "0",
                width: "100%",
                height: "100%",
              }}
            ></div>
          )}

          <div className={styles.root_content}>
            <div className={styles.root_header}>
              <div>
                <p className={styles.root_header_title_pre}>{t("ONE-TIME OFFER")}</p>
                <p className={styles.root_header_title}>{t("We Don’t Want to Lose You! 💙")}</p>
              </div>
              <p className={styles.root_header_paragraph}>
                {t(
                  "You mentioned Fortect felt too expensive, we get it. As a thank-you for your feedback, we’ll renew your plan at your original first-year price {{price}} and keep your protection active.",
                  {
                    price: formatPriceWithCurrency(renewalData?.price?.toString(), renewalData?.currency),
                  }
                )}
              </p>
            </div>

            <div className={styles.root_footer}>
              <Button
                onClick={() => showProcessingModal()}
                text={t("Lock My Price In {{price}}", {
                  price: formatPriceWithCurrency(renewalData?.price?.toString(), renewalData?.currency),
                })}
                type="primary"
                classNames={styles.root_footer_okMobile}
                isLoader={isLoading}
              />
              <Button
                onClick={() => noTrust()}
                text={t("No Thanks, Cancel Anyway")}
                type="error"
                classNames={styles.root_footer_cancelMobile}
              />
            </div>
          </div>

          <div className={styles.root_media}>
            <div className={styles.root_media_badge}>
              <img src={off} alt="fortect" />
              <div className={styles.root_media_badge_container}>
                <span>{t("Up to")}</span>
                <span className={styles.root_media_badge_container_number}>{t(renewalData?.percentage?.toString() + "%")}</span>
                <span>{t("OFF")}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CancelSubscriptionOfferV2Modal;
